import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Star, UserPlus } from "lucide-react";

const TARGET_FIELDS = [
    { key: "hires", label: "Hires" },
    { key: "revenue", label: "Revenue" },
    { key: "submissions", label: "Submissions" },
    { key: "interviews", label: "Interviews" },
    { key: "offers", label: "Offers" },
];

// Assign lead + collaborator recruiters to a job, with per-recruiter targets and a job-level SLA.
export default function AssignRecruitersDialog({ job, recruiters, defaultSla, open, onClose, onSaved }) {
    const [rows, setRows] = useState({});
    const [leadId, setLeadId] = useState(null);
    const [slaDays, setSlaDays] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !job) return;
        const map = {};
        let lead = null;
        (job.assignments || []).forEach((a) => {
            map[a.user_id] = { ...(a.targets || {}) };
            if (a.role === "lead") lead = a.user_id;
        });
        setRows(map);
        setLeadId(lead);
        setSlaDays(job.sla_days && job.sla_days !== defaultSla ? String(job.sla_days) : (job.sla_days && !defaultSla ? String(job.sla_days) : ""));
    }, [open, job, defaultSla]);

    const toggle = (uid) => {
        setRows((cur) => {
            const next = { ...cur };
            if (next[uid]) { delete next[uid]; if (leadId === uid) setLeadId(null); }
            else next[uid] = {};
            return next;
        });
    };

    const setTarget = (uid, key, val) => {
        setRows((cur) => ({ ...cur, [uid]: { ...cur[uid], [key]: val } }));
    };

    const save = async () => {
        setSaving(true);
        try {
            const assignments = Object.entries(rows).map(([uid, targets]) => ({
                user_id: uid,
                role: leadId === uid ? "lead" : "collaborator",
                targets: Object.fromEntries(
                    Object.entries(targets)
                        .filter(([, v]) => v !== "" && v !== null && v !== undefined)
                        .map(([k, v]) => [k, Number(v)])
                        .filter(([, v]) => !Number.isNaN(v))
                ),
            }));
            const payload = { assignments };
            if (slaDays === "") payload.clear_sla = true;
            else payload.sla_days = Number(slaDays);
            await api.patch(`/jobs/${job.id}/assignments`, payload);
            toast.success("Assignments saved");
            onSaved?.();
            onClose?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    if (!job) return null;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="assign-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <UserPlus className="w-4 h-4 text-ikb" /> Assign recruiters · {job.title}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-2">
                    {(recruiters || []).map((r) => {
                        const on = !!rows[r.id];
                        return (
                            <div key={r.id} className={`border p-3 transition-colors ${on ? "border-ikb bg-blue-50/40" : "border-border"}`} data-testid={`assign-row-${r.id}`}>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        checked={on}
                                        onChange={() => toggle(r.id)}
                                        className="w-4 h-4 accent-[#002FA7]"
                                        data-testid={`assign-check-${r.id}`}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <span className="font-medium text-sm">{r.name || r.email}</span>
                                        <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-foreground/60">{r.role}</span>
                                    </div>
                                    {on && (
                                        <button
                                            type="button"
                                            onClick={() => setLeadId(leadId === r.id ? null : r.id)}
                                            className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 border transition-colors ${leadId === r.id ? "border-amber-400 bg-amber-50 text-amber-700" : "border-border text-foreground/60 hover:border-amber-400 hover:text-amber-700"}`}
                                            data-testid={`assign-lead-${r.id}`}
                                        >
                                            <Star className={`w-3 h-3 ${leadId === r.id ? "fill-amber-500 text-amber-500" : ""}`} /> {leadId === r.id ? "Lead" : "Make lead"}
                                        </button>
                                    )}
                                </div>
                                {on && (
                                    <div className="mt-3 grid grid-cols-5 gap-2">
                                        {TARGET_FIELDS.map((f) => (
                                            <label key={f.key} className="block">
                                                <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">{f.label}</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={rows[r.id]?.[f.key] ?? ""}
                                                    onChange={(e) => setTarget(r.id, f.key, e.target.value)}
                                                    placeholder="—"
                                                    className="mt-1 w-full h-8 border border-border px-2 text-sm bg-white outline-none focus:border-ikb"
                                                    data-testid={`assign-target-${r.id}-${f.key}`}
                                                />
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <p className="text-[11px] text-foreground/60">Targets are optional — whatever you enter gets tracked on the pipeline. Blank fields stay hidden.</p>
                </div>

                <div className="border-t border-border pt-4">
                    <label className="block max-w-xs">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">SLA — days to fill (job override)</span>
                        <input
                            type="number"
                            min="1"
                            max="365"
                            value={slaDays}
                            onChange={(e) => setSlaDays(e.target.value)}
                            placeholder={defaultSla ? `Company default: ${defaultSla}d` : "No SLA"}
                            className="mt-1 w-full h-9 border border-border px-2 text-sm bg-white outline-none focus:border-ikb"
                            data-testid="assign-sla-input"
                        />
                    </label>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="assign-dialog-cancel">Cancel</Button>
                    <Button onClick={save} disabled={saving} className="bg-ikb hover:bg-[#001f7a]" data-testid="assign-dialog-save">
                        {saving ? "Saving…" : "Save assignments"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
